import { useEffect, useRef } from 'react';
import { gsap, ScrollTrigger } from '@/hooks/useGsapAnimations';

gsap.registerPlugin(ScrollTrigger);

const Testimonials = () => {
    const sectionRef = useRef<HTMLElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const gridRef = useRef<HTMLDivElement>(null);

    const testimonials = [
        {
            name: "Sarah Chen",
            role: "High School Student",
            avatar: "S",
            rating: 5,
            text: "Eddy AI helped me solve my maths questions easily.The personalized quizzes are also amazing!",
            gradient: "from-[#8E57FA] to-[#B57FFA]"
        },
        {
            name: "Michael Torres",
            role: "College Student",
            avatar: "M",
            rating: 5,
            text: "The question scanning feature is a lifesaver during study sessions. Quick, accurate, and the explanations are so clear.",
            gradient: "from-[#1a1425] to-[#1a1425]"
        },
        {
            name: "Emily Watson",
            role: "Parent",
            avatar: "E",
            rating: 5,
            text: "My daughter's confidence has grown so much since using this app. The personalized approach really makes a difference.",
            gradient: "from-[#1a1425] to-[#1a1425]"
        }
    ];

    useEffect(() => {
        const el = sectionRef.current;
        if (!el) return;

        const ctx = gsap.context(() => {
            // Header fade in
            if (headerRef.current) {
                gsap.fromTo(
                    headerRef.current,
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.8,
                        ease: 'power2.out',
                        scrollTrigger: {
                            trigger: headerRef.current,
                            start: 'top 85%',
                            toggleActions: 'play none none none',
                        },
                    }
                );
            }

            // Cards stagger in from bottom
            if (gridRef.current) {
                const cards = gridRef.current.querySelectorAll('.testimonial-card');
                gsap.fromTo(
                    cards,
                    { opacity: 0, y: 50 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.7,
                        ease: 'power2.out',
                        stagger: 0.2,
                        scrollTrigger: {
                            trigger: gridRef.current,
                            start: 'top 85%',
                            toggleActions: 'play none none none',
                        },
                    }
                );
            }
        }, el);

        return () => ctx.revert();
    }, []);

    return (
        <section ref={sectionRef} id="testimonials" className="relative z-10 px-4 sm:px-6 lg:px-20 py-20 md:py-20 lg:py-24  max-w-[1440px] mx-auto">
            {/* Section Header */}
            <div ref={headerRef} className="text-center mb-12 sm:mb-16 space-y-3 sm:space-y-2" style={{ opacity: 0 }}>
                <h2
                    className="text-white"
                    style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: 500,
                        fontSize: "clamp(28px, 6vw, 48px)",
                        lineHeight: "1.3",
                        letterSpacing: "0px",
                    }}
                >
                    Testimonials
                </h2>
                <p
                    className="text-[#DCCBFD]"
                    style={{
                        fontFamily: "'Roboto', sans-serif",
                        fontWeight: 400,
                        fontSize: "clamp(14px, 3.5vw, 16px)",
                        lineHeight: "clamp(22px, 5vw, 26px)",
                        letterSpacing: "0.3px",
                    }}
                >
                    Loved by Students Everywhere
                </p>
            </div>

            {/* Testimonials Grid */}
            <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                {testimonials.map((testimonial, index) => (
                    <div
                        key={index}
                        className="testimonial-card group rounded-3xl p-6 sm:p-8 border border-[#FFFFFF1A] backdrop-blur-sm transition-all duration-300 hover:scale-105 hover:border-[#8E57FA]/50 bg-[#1a1425]/40 hover:bg-gradient-to-b hover:from-[#8E57FA4D]/70 hover:to-[#8E57FA4D]/10 hover:shadow-[0_20px_40px_rgba(142,87,250,0.2)]"
                        style={{
                            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
                            opacity: 0,
                        }}
                    >
                        {/* Star Rating */}
                        <div className="flex gap-1 mb-4 sm:mb-6">
                            {[...Array(testimonial.rating)].map((_, i) => (
                                <svg
                                    key={i}
                                    className="w-5 h-5 sm:w-6 sm:h-6 transition-colors duration-300 group-hover:fill-white"
                                    fill="#8E57FA"
                                    viewBox="0 0 20 20"
                                >
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                            ))}
                        </div>

                        {/* Testimonial Text */}
                        <p
                            className="mb-6 sm:mb-8 transition-colors duration-300 text-[#DCCBFD] group-hover:text-white/90"
                            style={{
                                fontFamily: "'Roboto', sans-serif",
                                fontWeight: 400,
                                fontSize: "clamp(13px, 3vw, 15px)",
                                lineHeight: "clamp(22px, 5vw, 26px)",
                                letterSpacing: "0.3px",
                            }}
                        >
                            "{testimonial.text}"
                        </p>

                        {/* Author Info */}
                        <div className="flex items-center gap-3 sm:gap-4">
                            {/* Avatar */}
                            <div
                                className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-white transition-colors duration-300 bg-[#8E57FA] "
                                style={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontWeight: 600,
                                    fontSize: "clamp(18px, 4vw, 20px)",
                                }}
                            >
                                {testimonial.avatar}
                            </div>

                            {/* Name and Role */}
                            <div className="flex-1 min-w-0">
                                <h4
                                    className="text-white truncate transition-colors duration-300"
                                    style={{
                                        fontFamily: "'Poppins', sans-serif",
                                        fontWeight: 600,
                                        fontSize: "clamp(14px, 3.5vw, 16px)",
                                        lineHeight: "1.4",
                                    }}
                                >
                                    {testimonial.name}
                                </h4>
                                <p
                                    className="truncate transition-colors duration-300 text-[#DCCBFD] group-hover:text-white/70"
                                    style={{
                                        fontFamily: "'Roboto', sans-serif",
                                        fontWeight: 400,
                                        fontSize: "clamp(12px, 2.5vw, 13px)",
                                        lineHeight: "1.5",
                                    }}
                                >
                                    {testimonial.role}
                                </p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};

export default Testimonials;
